import { ModulePaywall } from '@app/shared/components/module-paywall/module-paywall';
import { PRIVATE_PATH } from '@app/shared/constants/paths.constant';
import { useModulePaywall } from '@app/shared/hooks/use-module-paywall.hook';
import { useMySubscription } from '@app/shared/hooks/subscription.hook';
import { Business } from '@app/shared/models/business.model';
import { i18n } from '@i18n/translations';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@neogeniux/uikit';
import { FC, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

type BusinessPlan = Business['plan'];

const planRank: Record<BusinessPlan, number> = {
  FREE: 0,
  STARTUP: 1,
  PROFESIONAL: 2,
};

const hasRequiredPlan = (
  currentPlan: BusinessPlan,
  requiredPlan: BusinessPlan
) => {
  return planRank[currentPlan] >= planRank[requiredPlan];
};

export interface PlanGuardProps {
  requiredPlan: BusinessPlan;
  /**
   * Optional capability SKU (e.g. `addon.kds`, `module.pos`). When
   * provided, the "no access" state renders <ModulePaywall> instead
   * of the legacy "Actualización requerida" Card — operators get the
   * catalog name + monthly price + inline Agregar CTA instead of
   * being told to inscribe a tarjeta and go change the plan
   * themselves. Callers without an SKU keep the legacy fallback.
   */
  sku?: string;
  /**
   * Optional features array forwarded to ModulePaywall when `sku` is
   * set. Free-form Spanish strings (no i18n coupling) so each route
   * can localize its own pitch.
   */
  paywallFeatures?: string[];
  /**
   * Optional pitch forwarded to ModulePaywall.
   */
  paywallPitch?: string;
  children: ReactNode;
}

export const PlanGuard: FC<PlanGuardProps> = ({
  requiredPlan,
  sku,
  paywallFeatures,
  paywallPitch,
  children,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const { data: subscription, isLoading } = useMySubscription();
  const paywall = useModulePaywall(sku ?? 'noop');

  const currentPlan = (subscription?.plan || 'FREE') as BusinessPlan;
  const isSponsored = subscription?.isSponsored === true;

  // When a capability SKU is supplied, ownership of that SKU (or one
  // of its catalog equivalents — addon.kds ↔ module.kds, etc.) wins
  // over the legacy plan-rank check. New tenants on Negocio /
  // Empresarial still carry `business.plan='FREE'` even though their
  // subscription items already include `module.kds`; the plan-rank
  // gate was rejecting them and surfacing ModulePaywall on /kds.
  const hasAccess = useMemo(() => {
    if (isSponsored) return true;
    if (sku && paywall.hasAccess) return true;
    return hasRequiredPlan(currentPlan, requiredPlan);
  }, [currentPlan, requiredPlan, isSponsored, sku, paywall.hasAccess]);

  if (isLoading || (sku && paywall.isLoading)) {
    return null;
  }

  if (hasAccess) {
    return children;
  }

  if (sku) {
    return (
      <ModulePaywall
        sku={sku}
        pitch={paywallPitch}
        features={paywallFeatures}
      />
    );
  }

  return (
    <div className="flex justify-center">
      <Card className="w-full max-w-[640px]">
        <CardHeader>
          <CardTitle>{t(i18n.SUBSCRIPTION.ACCESS.TITLE)}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-sm text-muted-foreground">
            {t(i18n.SUBSCRIPTION.ACCESS.DESCRIPTION, {
              plan: t(i18n.SIDEBAR[requiredPlan]),
            })}
          </p>
          <p className="text-sm text-muted-foreground">
            {t(i18n.SUBSCRIPTION.ACCESS.STEPS)}
          </p>

          <div className="pt-2">
            <Button
              type="button"
              onClick={() => {
                navigate({
                  pathname: PRIVATE_PATH.ACCOUNT,
                  search: `?requiredPlan=${requiredPlan}&from=${encodeURIComponent(
                    location.pathname
                  )}`,
                });
              }}
            >
              {t(i18n.SUBSCRIPTION.ACCESS.CTA)}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PlanGuard;
